/**
 * "How many more spaces can I approve" — a supplier's remaining, approvable
 * capacity right now.
 *
 * This is deliberately a hand-written skill rather than a report descriptor,
 * because the answer is a *difference between two sources*, not a row count:
 *
 *   available = total capacity (the listing's `total_number_of_spaces`)
 *             − current occupancy (equipment on site right now)
 *
 * Capacity comes for free from the bulk-edit export already in hand
 * (`context.valueFor`), exactly as {@link totalSpaces} reads it. Occupancy is
 * the same live snapshot the current-inventory report counts
 * (`/api/suppliers/current-inventory`). Keeping both here means the wording can
 * say plainly what it subtracted, which a generic count template could not.
 *
 * It sits between two neighbours it must not be confused with: `totalSpaces`
 * answers capacity alone, and `supplierCurrentInventory` answers occupancy
 * alone. This one is the remainder, and only a supplier — who owns the
 * locations and does the approving — can ask it.
 */

import { supplierCurrentInventoryReportEndpoints } from '../../requests/api-endpoints';
import { countReportRows } from './requests/report-count-requests';
import { coerceCount } from '../../requests/generic-requests';
import type { KnowledgeAnswer, KnowledgeContext, KnowledgeQuery } from './knowledgeRegistry';

/** Export column holding a location's listed capacity. */
const CAPACITY_COLUMN = 'total_number_of_spaces';

/**
 * Most locations the answer will make an occupancy call for before it stops
 * breaking down. Each named location is its own request, so a large portfolio
 * would otherwise fire dozens of calls to render one answer. Kept in step with
 * the registry's own fan-out ceiling.
 */
const MAX_BREAKDOWN_REQUESTS = 12;

const pluralize = (count: number, singular: string): string =>
  `${count} ${singular}${count === 1 ? '' : 's'}`;

/**
 * Reads a count out of a report search response, tolerating all three shapes
 * the count endpoints use (`{ count }`, a value object, or a plain number) —
 * the same reconciliation {@link countReportRows}'s callers rely on.
 */
const readCount = (response: { body?: unknown; text?: unknown } | undefined): number =>
  coerceCount(response?.body) ?? coerceCount(response?.text) ?? 0;

/** Live occupancy — equipment on site now — for one location or the whole account. */
const occupancyFor = async (locationId?: string): Promise<number> =>
  readCount(
    await countReportRows(
      supplierCurrentInventoryReportEndpoints.search,
      locationId ? { locationId } : {},
    ),
  );

/** Subject and its verb agreement, so one formatter serves "You"/"Cheyenne". */
interface SubjectForms {
  subject: string;
  /** "is" for a single location, "are" for the account as a whole. */
  be: string;
  /** "has" for a single location, "have" for the account. */
  have: string;
}

/**
 * One sentence describing what a subject can still approve, spelling out the two
 * numbers it was derived from so a supplier can sanity-check it.
 *
 * Three cases carry their own wording: an unknown capacity cannot be answered at
 * all (better than implying zero), being at or over capacity reads as a state
 * rather than a bare "0", and the ordinary case leads with the number asked for.
 */
const describeAvailability = (
  forms: SubjectForms,
  capacity: number,
  occupancy: number,
): string => {
  const { subject, be, have } = forms;
  const available = Math.max(0, capacity - occupancy);

  if (capacity <= 0) {
    return (
      `${subject} ${have} no capacity on file, so I cannot tell how many more ` +
      'spaces you can approve. Set a total number of spaces on the location first.'
    );
  }

  if (available === 0) {
    const over = occupancy - capacity;
    const overNote = over > 0 ? ` — ${pluralize(over, 'space')} over capacity` : '';
    return (
      `${subject} ${be} at capacity right now: **${capacity}** ` +
      `${capacity === 1 ? 'space' : 'spaces'} with **${occupancy}** on site${overNote}, ` +
      'so there are **0** more to approve.'
    );
  }

  return (
    `${subject} can approve **${pluralize(available, 'more space')}** right now — ` +
    `**${capacity}** total, **${occupancy}** currently on site.`
  );
};

/** Whole-account wording: first-person subject, plural agreement. */
const accountAvailability = (capacity: number, occupancy: number): string =>
  describeAvailability({ subject: 'You', be: 'are', have: 'have' }, capacity, occupancy);

/** Single-location wording: the location's name, singular agreement. */
const locationAvailability = (name: string, capacity: number, occupancy: number): string =>
  describeAvailability({ subject: `**${name}**`, be: 'is', have: 'has' }, capacity, occupancy);

const capacityOf = (context: KnowledgeContext, locationId: string): number =>
  Number(context.valueFor(locationId, CAPACITY_COLUMN)) || 0;

/** Per-location availability, one occupancy call each. */
const availabilityRows = (
  context: KnowledgeContext,
  targets: KnowledgeContext['targets'],
): Promise<Array<{ name: string; available: number }>> =>
  Promise.all(
    targets.map(async (target) => {
      const capacity = capacityOf(context, target.id);
      const occupancy = await occupancyFor(target.id);
      return {
        name: target.name,
        available: capacity <= 0 ? 0 : Math.max(0, capacity - occupancy),
      };
    }),
  );

/**
 * A total plus a per-location breakdown. The total is the **sum of the rows**,
 * not capacity−occupancy across the account: a location that is over capacity
 * cannot lend its overflow to another, so clamping each location at zero first
 * is the honest count of what can actually be approved.
 */
const breakdownAnswer = (
  rows: Array<{ name: string; available: number }>,
  wholeAccount: boolean,
): KnowledgeAnswer => {
  const total = rows.reduce((sum, row) => sum + row.available, 0);
  const text = wholeAccount
    ? `You can approve **${pluralize(total, 'more space')}** right now across your ` +
      `${pluralize(rows.length, 'location')}:`
    : `Those ${pluralize(rows.length, 'location')} have **${pluralize(
        total,
        'space',
      )}** you can approve right now, between them:`;
  return {
    text,
    breakdown: rows.map((row) => ({ label: row.name, value: String(row.available) })),
  };
};

const answer = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const { targets, wholeAccount } = context;

  if (wholeAccount) {
    // Small enough to break down per location without a burst of requests —
    // the user asked for the split, so give the total and each location's share.
    if (targets.length > 1 && targets.length <= MAX_BREAKDOWN_REQUESTS) {
      return breakdownAnswer(await availabilityRows(context, targets), true);
    }

    // A single location, or a portfolio too large to fan out over: capacity is
    // summed from the export (free) and occupancy is one account-wide call.
    const capacity = targets.reduce((sum, target) => sum + capacityOf(context, target.id), 0);
    const occupancy = await occupancyFor();
    const summary = accountAvailability(capacity, occupancy);
    if (targets.length > MAX_BREAKDOWN_REQUESTS) {
      return {
        text:
          `${summary} That is across ${targets.length} locations — more than I break down ` +
          'in one answer, so ask about a single location for its own number.',
      };
    }
    return { text: summary };
  }

  if (targets.length === 1) {
    const target = targets[0];
    const capacity = capacityOf(context, target.id);
    const occupancy = await occupancyFor(target.id);
    return { text: locationAvailability(target.name, capacity, occupancy) };
  }

  // A named or ambiguous set larger than the fan-out ceiling cannot be broken
  // down without a burst of calls, and its occupancy cannot be had in one call
  // the way the whole account's can, so it asks to be narrowed rather than
  // guessing.
  if (targets.length > MAX_BREAKDOWN_REQUESTS) {
    return {
      text:
        `That is more than ${MAX_BREAKDOWN_REQUESTS} locations, so I have not worked out ` +
        'the availability for each — ask about one location for the spaces you can approve there.',
    };
  }

  return breakdownAnswer(await availabilityRows(context, targets), false);
};

/**
 * Supplier-only. A buyer books spaces and an admin oversees the platform;
 * neither approves reservations against a location's capacity, which is what
 * this question is about.
 */
export const availableSpacesQuery: KnowledgeQuery = {
  id: 'availableSpaces',
  audiences: ['supplier'],
  synonyms: [
    'how many more spaces can i approve',
    'how many spaces can i approve',
    'how many more spaces can i accept',
    'how many spaces can i accept',
    'how many more spaces are available',
    'how many spaces are available',
    'how many spaces do i have available',
    'how many spaces are left',
    'how many spaces are open',
    'how many open spaces do i have',
    'spaces available to approve',
    'available spaces to approve',
    'how much space do i have left',
    'how many more can i approve',
    // Phrased as "bookings" rather than "spaces": a supplier approves a booking
    // *into* a space, so "can I approve additional bookings" is the same
    // remaining-capacity question. Kept clear of the pending-approvals work
    // queue ("bookings waiting for approval", "need approving"), which asks how
    // many are already in front of them, not how many more they could take.
    'approve additional bookings',
    'approve more bookings',
    'approve another booking',
    'accept additional bookings',
    'accept more bookings',
    'take more bookings',
    'take additional bookings',
    'bookings can i approve',
    'bookings can i accept',
    'bookings can i take',
    'room for more bookings',
  ],
  // Concept fallback for phrasings the synonyms miss: an availability/approval
  // word together with a space word. Narrow on purpose — it must not swallow
  // "how many spaces do I have" (capacity) or the yard-occupancy question, so it
  // requires a word that specifically means "still open to approve".
  requires: [
    ['available', 'left', 'remaining', 'free', 'open', 'approve', 'approvals', 'accept'],
    ['space', 'spaces'],
  ],
  example: 'how many more spaces can I approve',
  description:
    'How many more spaces a supplier can still approve right now, and so whether they can ' +
    'approve or take additional bookings: their listed capacity (total number of spaces) ' +
    'minus what is currently on site (occupancy). The remainder between the total-spaces ' +
    'question and the equipment-on-site question — not the total capacity on its own, not ' +
    'the count on the yard on its own, and not the number of bookings awaiting approval ' +
    '(that work queue is a different skill). A live snapshot, so it ignores any time period.',
  answer,
};

export const __testing = {
  describeAvailability,
  accountAvailability,
  locationAvailability,
  occupancyFor,
};
